import { defineStore } from "pinia";
import { ref, computed, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import type { LocationQueryValue } from "vue-router";
import type { MenuItem } from "primevue/menuitem";
import type { PageState } from "primevue/paginator";
import { useUserStore } from "./user.store.ts";
import { useTimeoutFn, useTitle } from "@vueuse/core";
import { DEFAULT_PAGE_LIMIT } from "../helpers/pagination.js";

export const useUiStore = defineStore("ui", () => {
  const router = useRouter();
  const route = useRoute();
  const topbarTitle = ref("");
  const celebrating = ref(false);
  const userStore = useUserStore();
  const limit = ref(DEFAULT_PAGE_LIMIT);
  watch(
    () => userStore.user,
    (user) => {
      limit.value = user?.limit ?? DEFAULT_PAGE_LIMIT;
    },
    { immediate: true, flush: "sync" },
  );
  const selection = ref<Record<string, unknown>[]>([]);
  const actions = ref<MenuItem[]>([]);
  const showPopover = ref(true);

  const pageTitle = computed(() => {
    return topbarTitle.value ? `Dubrink - ${topbarTitle.value}` : "Dubrink - Intelligent Trade Compliance";
  });

  useTitle(pageTitle);

  const clear = () => {
    selection.value = [];
    actions.value = [];
    showPopover.value = true;
  };

  const { start: stopCelebratingAfterDelay } = useTimeoutFn(() => (celebrating.value = false), 5000, {
    immediate: false,
  });

  const celebrate = () => {
    celebrating.value = true;
    stopCelebratingAfterDelay();
  };

  const paginate = (e: PageState) => {
    router.push({ query: { ...route.query, page: e.page + 1 } });
  };

  const getPaginationState = (page: LocationQueryValue | LocationQueryValue[] | number | undefined) =>
    (page ? Number(page) - 1 : 0) * limit.value;

  return {
    celebrate,
    celebrating,
    topbarTitle,
    selection,
    actions,
    clear,
    showPopover,
    limit,
    getPaginationState,
    paginate,
  };
});
